import { Tab, Theme, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { AggregationItem, important } from 'common-types';
import { useCheckboxGroup } from 'react-utils';
import Aggregations from '../../../../elements/Aggregation/AggregationsTable';
import CustomLink from '../../../../elements/Aggregation/elements/Link';
import { setSelectedAggs } from '../../store-actions/issues-exclusions-store-actions';
import { IssueDetails } from '../../types/issues-types';
import IssueCardAdditionalInfo from '../IssueCard/IssueCardAdditionalInfo';
import IssueCardPolicyInfo from '../IssueCard/IssueCardPolicyInfo';
import IssueActionsTable from './IssueActionsTable/IssueActionsTable';
import SeverityChangeReason from './SeverityChangeReason/SeverityChangeReasonList';
import config from '../../config/aggregations-columns.json';
import OxTabs from '../../../../elements/OxTabs/OxTabs';
import { useTableConfig } from './ScaVulnerabilitiesTable/useTableConfig';
import OxDataGrid from '../../../../elements/OxDataGrid/OxDataGrid';
import VulnerabilityGraph from './ScaVulnerabilitiesTable/VulnerabilityGraph';
import { browserName, BrowserTypes } from 'react-device-detect';
import IssueSummary from './IssueSummary';
import { isDevelopment } from 'env-utils';
import useOxNamedTabs from '../../../../elements/OxTabs/hooks/useOxNamedTabs';

const IssueCard: FC<React.PropsWithChildren<IssueCardProps>> = ({
  issueDetails,
  onComment,
  loadingJiraTicketsStatus,
  isActiveJiraConnection,
}) => {
  const { classes } = useStyles();
  const { selected, allSelected, toggle, toggleAll, updateGroup } =
    useCheckboxGroup(issueDetails.aggregations?.items?.length, true);
  const scaColumns = useTableConfig(classes);
  useEffect(() => {
    updateGroup(issueDetails.aggregations.items.length);
  }, [issueDetails.aggregations.items, updateGroup]);

  useEffect(() => {
    const selectedAggs = selected.reduce((acc, _, index) => {
      if (selected[index]) {
        acc.push(issueDetails.aggregations.items[index]);
      }
      return acc;
    }, [] as AggregationItem[]);
    setSelectedAggs(selectedAggs);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const tabs: { title: string; component: JSX.Element }[] = useMemo(() => {
    const t: { title: string; component: JSX.Element }[] = [];
    t.push({
      title: 'Summary',
      component: (
        <IssueSummary
          key='Summary'
          issueDetails={issueDetails}
          onComment={onComment}
        />
      ),
    });

    issueDetails.directSCAVulnerability?.length &&
      t.push({
        title: 'Reachable Vulnerabilities',
        component: (
          <OxDataGrid
            key={'Reachable Vulnerabilities'}
            className={classes.table}
            columns={scaColumns}
            rows={issueDetails.directSCAVulnerability}
            rowHeight={70}
            headerRowHeight={35}
          />
        ),
      });
    if (!isDevelopment()) {
      issueDetails.scaVulnerabilities?.length &&
        t.push({
          title: 'Vulnerabilities',
          component: (
            <OxDataGrid
              key={'Vulnerabilities'}
              className={classes.table}
              columns={scaColumns}
              rows={issueDetails.scaVulnerabilities}
              rowHeight={70}
              headerRowHeight={35}
            />
          ),
        });
    }

    issueDetails.noneDirectSCAVulnerability?.length &&
      t.push({
        title: 'Not Reachable Vulnerabilities',
        component: (
          <OxDataGrid
            key={'Not Reachable Vulnerabilities'}
            className={classes.table}
            columns={scaColumns}
            rows={issueDetails.noneDirectSCAVulnerability}
            rowHeight={70}
            headerRowHeight={35}
          />
        ),
      });

    issueDetails.dependencyGraphNodes?.length &&
      t.push({
        title: 'Dependency Graph',
        component: (
          <div
            key={'Dependency Graph'}
            style={{ height: '100%', width: '100%' }}>
            <VulnerabilityGraph
              nodes={issueDetails.dependencyGraphNodes}
              edges={issueDetails.dependencyGraphEdges}
            />
          </div>
        ),
      });

    issueDetails.aggregations?.items?.length &&
      t.push({
        title: 'Aggregations',
        component: (
          <Aggregations
            key='Aggregations'
            config={config[issueDetails.aggregations.type]}
            info={issueDetails.violationInfoTitle}
            items={issueDetails.aggregations.items}
            selected={selected}
            allSelected={allSelected}
            toggle={toggle}
            toggleAll={toggleAll}
            isInfo
          />
        ),
      });

    issueDetails.cweList?.length &&
      t.push({
        title: 'CWE',
        component: (
          <>
            {issueDetails.cweList.map(type => {
              return (
                <Tooltip
                  key={type.name}
                  title={
                    <Typography color='text.primary' variant='body2'>
                      {type.description}
                    </Typography>
                  }
                  arrow
                  placement='top'>
                  <div>
                    <CustomLink value={type.name} href={type.url} />
                  </div>
                </Tooltip>
              );
            })}
          </>
        ),
      });

    issueDetails.severityChangedReason?.length &&
      t.push({
        title: 'Severity Change',
        component: (
          <SeverityChangeReason
            key={'Severity Change'}
            data={issueDetails.severityChangedReason}
            withDescription
          />
        ),
      });

    isActiveJiraConnection &&
      issueDetails.tickets.length &&
      t.push({
        title: 'JIRA',
        component: (
          <IssueActionsTable
            key='JIRA'
            loading={!!loadingJiraTicketsStatus}
            data={issueDetails.tickets}
          />
        ),
      });

    t.push({
      title: 'Additional Info',
      component: (
        <IssueCardAdditionalInfo
          key='Additional Info'
          violationInfoTitle={''}
          sourceTools={issueDetails.sourceTools}
          cwe={issueDetails.cweList}
          extraInfo={issueDetails.extraInfo}
          fixLink={issueDetails.fixLink}
        />
      ),
    });
    t.push({
      title: 'Policy Info',
      component: (
        <IssueCardPolicyInfo
          key='Policy Info'
          policyInfo={issueDetails.policy}
          categoryId={issueDetails.category.categoryId}
        />
      ),
    });

    return t;
  }, [
    allSelected,
    classes.table,
    isActiveJiraConnection,
    issueDetails,
    loadingJiraTicketsStatus,
    onComment,
    scaColumns,
    selected,
    toggle,
    toggleAll,
  ]);

  const { selectedKey, handleTabChange } = useOxNamedTabs('Summary');

  useEffect(() => {
    if (!tabs.some(({ title }) => selectedKey === title)) {
      handleTabChange(null, 'Summary');
    }
  }, [handleTabChange, selectedKey, tabs]);
  return (
    <>
      <OxTabs
        className={classes.tabs}
        selectedTab={selectedKey}
        onChange={handleTabChange}>
        {tabs.map(({ title }) => (
          <Tab key={title} value={title} label={title} sx={{ fontSize: 12 }} />
        ))}
      </OxTabs>
      <div style={{ padding: 16, height: 'calc(100% - 60px)' }}>
        {tabs.map(
          ({ component, title }, i) => selectedKey === title && component,
        )}
      </div>
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  tabs: {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.modal,
    backgroundColor: theme.palette.background.paper,
  },
  issueCard: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
  },
  appTypeSummeryScore: {
    display: 'flex',
  },
  appTypeSummery: {
    display: 'flex',
  },
  repoSummery: {
    display: 'flex',
    flexDirection: 'row',
  },
  twoCols: {
    display: 'flex',
    '& > div': {
      flex: '1 1 100%',
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  issueActions: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  category: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  categoryIcon: {
    width: '1em',
    height: '1em',
    flexShrink: 0,
    color: theme.palette.primary.main,
  },
  summaryContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
  text: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    display: '-webkit-box',
    whiteSpace: 'pre-wrap',
    lineClamp: 2,
    WebkitLineClamp: 2,
    lineHeight: 1,
    WebkitBoxOrient: 'vertical',
  },
  wrapper: {
    ...(browserName === BrowserTypes.Firefox
      ? {
          height: 200,
        }
      : { maxHeight: 500 }),
    overflow: 'auto',
  },
  table: {
    height: '100%',

    '--rdg-header-background-color': important(
      theme.palette.background.default,
    ),
  },
}));

export interface IssueCardProps {
  issueDetails: IssueDetails;
  onComment?: () => void;
  loadingJiraTicketsStatus?: boolean;
  isActiveJiraConnection?: boolean;
}

export default IssueCard;
